import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  LineProps
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Warning,
  CheckCircle,
  Error
} from '@mui/icons-material';
import { format } from 'date-fns';

interface MiniChartData {
  timestamp: string;
  meter_factor: number;
  ucl: number;
  lcl: number;
  center: number;
}

interface MiniMeterChartProps {
  meterProductId: string;
  meterId: string;
  productType: string;
  data: MiniChartData[];
  loading?: boolean;
  onClick?: () => void;
  statistics?: {
    current_health: 'green' | 'yellow' | 'red';
    trend: 'increasing' | 'decreasing' | 'stable';
    anomaly_count: number;
    days_since_proving: number;
  };
}

const MiniMeterChart: React.FC<MiniMeterChartProps> = ({
  meterProductId,
  meterId,
  productType,
  data,
  loading = false,
  onClick,
  statistics
}) => {
  // Get health color
  const getHealthColor = () => {
    if (!statistics) return '#9e9e9e';
    switch (statistics.current_health) {
      case 'green': return '#4caf50';
      case 'yellow': return '#ff9800';
      case 'red': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  // Get health icon
  const getHealthIcon = () => {
    if (!statistics) return null;
    switch (statistics.current_health) {
      case 'green': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'yellow': return <Warning sx={{ fontSize: 16 }} />;
      case 'red': return <Error sx={{ fontSize: 16 }} />;
      default: return null;
    }
  };

  // Get trend icon
  const getTrendIcon = () => {
    if (!statistics) return <TrendingFlat sx={{ fontSize: 14 }} color="disabled" />;
    switch (statistics.trend) {
      case 'increasing': return <TrendingUp sx={{ fontSize: 14 }} color="warning" />;
      case 'decreasing': return <TrendingDown sx={{ fontSize: 14 }} color="warning" />;
      case 'stable': return <TrendingFlat sx={{ fontSize: 14 }} color="success" />;
      default: return <TrendingFlat sx={{ fontSize: 14 }} color="disabled" />;
    }
  };

  // Simplified tooltip content
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length > 0) {
      // Find the meter_factor_value in the payload
      const meterFactorData = payload.find((p: any) => p.dataKey === 'meter_factor_value');
      const value = meterFactorData ? meterFactorData.value : (payload[0].payload?.meter_factor || payload[0].value);

      return (
        <Box sx={{
          bgcolor: 'background.paper',
          p: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1
        }}>
          <Typography variant="caption">
            {label ? format(new Date(label), 'MMM dd') : ''}
          </Typography>
          <Typography variant="caption" display="block" color="primary">
            MF: {typeof value === 'number' ? value.toFixed(4) : value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          height: 180,
          cursor: 'pointer'
        }}
        onClick={onClick}
      >
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 1 }} />
      </Box>
    );
  }

  // Sort data chronologically to get the actual latest reading
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.timestamp).getTime();
    const dateB = new Date(b.timestamp).getTime();
    return dateA - dateB;
  });

  const latestDataPoint = sortedData.length > 0 ? sortedData[sortedData.length - 1] : null;
  const latestFactor = latestDataPoint ? latestDataPoint.meter_factor : null;
  const isOutOfControl = latestDataPoint && latestFactor !== null &&
    (latestFactor > latestDataPoint.ucl || latestFactor < latestDataPoint.lcl);

  // Check if chart needs to be zoomed out
  const factors = sortedData.slice(-5).map(d => d.meter_factor);
  const ucl = data[0]?.ucl || 0;
  const lcl = data[0]?.lcl || 0;
  const controlRange = ucl - lcl;
  const dataRange = factors.length > 0 ? Math.max(...factors) - Math.min(...factors) : 0;
  const isZoomedOut = dataRange > controlRange * 2;

  return (
    <Box
      sx={{
        p: 1.5,
        border: 2,
        borderColor: isOutOfControl ? 'error.main' : getHealthColor(),
        borderRadius: 1,
        height: 180,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.main',
        }
      }}
      onClick={onClick}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" fontWeight="bold">
            {meterId}
          </Typography>
          <Chip
            label={productType}
            size="small"
            sx={{ height: 16, fontSize: '0.65rem' }}
            color="primary"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getHealthIcon()}
          {getTrendIcon()}
        </Box>
      </Box>

      {/* Current Factor Display */}
      {latestFactor && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 0.5,
            color: isOutOfControl ? 'error.main' : 'text.secondary'
          }}
        >
          MF: {typeof latestFactor === 'number' ? latestFactor.toFixed(4) : latestFactor}
          {statistics && statistics.days_since_proving > 0 && (
            <Typography variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
              ({statistics.days_since_proving}d ago)
            </Typography>
          )}
        </Typography>
      )}

      {/* Mini Chart - Show only last 5 points for clarity */}
      <ResponsiveContainer width="100%" height={100}>
        <ComposedChart
          data={sortedData.slice(-5).map((point, index, arr) => {
            // Ensure every data point has control limit values
            // Use the first data point's values as defaults if missing
            const firstPoint = data[0] || {};
            const ucl = point.ucl !== undefined ? point.ucl : firstPoint.ucl;
            const lcl = point.lcl !== undefined ? point.lcl : firstPoint.lcl;
            const isOutOfControl = point.meter_factor > ucl || point.meter_factor < lcl;

            return {
              ...point,
              ucl: ucl,
              lcl: lcl,
              center: point.center !== undefined ? point.center : firstPoint.center,
              // Just use the raw meter factor for a single continuous line
              meter_factor_value: point.meter_factor,
              // For dots
              is_out_of_control: isOutOfControl
            };
          })}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          {/* Control limit lines - always render if we have the values */}
          {data.length > 0 && data[0] && (
            <>
              {/* UCL - Upper Control Limit */}
              {(data[0].ucl !== undefined) && (
                <Line
                  type="monotone"
                  dataKey="ucl"
                  stroke="#ff6b6b"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  dot={false}
                  legendType="none"
                  isAnimationActive={false}
                  connectNulls={true}
                />
              )}
              {/* LCL - Lower Control Limit */}
              {(data[0].lcl !== undefined) && (
                <Line
                  type="monotone"
                  dataKey="lcl"
                  stroke="#ff6b6b"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  dot={false}
                  legendType="none"
                  isAnimationActive={false}
                  connectNulls={true}
                />
              )}
              {/* Center Line */}
              {(data[0].center !== undefined) && (
                <Line
                  type="monotone"
                  dataKey="center"
                  stroke="#999"
                  strokeDasharray="5 5"
                  strokeWidth={0.5}
                  dot={false}
                  legendType="none"
                  isAnimationActive={false}
                  connectNulls={true}
                />
              )}
            </>
          )}

          {/* Fallback: Use ReferenceLine if control limits are not in data but we have the values */}
          {data.length > 0 && data[0] && (!data[0].ucl || !data[0].lcl || !data[0].center) && (
            <>
              {latestDataPoint && latestDataPoint.ucl !== undefined && (
                <ReferenceLine
                  y={latestDataPoint.ucl}
                  stroke="#ff6b6b"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  label=""
                />
              )}
              {latestDataPoint && latestDataPoint.lcl !== undefined && (
                <ReferenceLine
                  y={latestDataPoint.lcl}
                  stroke="#ff6b6b"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  label=""
                />
              )}
              {latestDataPoint && latestDataPoint.center !== undefined && (
                <ReferenceLine
                  y={latestDataPoint.center}
                  stroke="#999"
                  strokeDasharray="5 5"
                  strokeWidth={0.5}
                  label=""
                />
              )}
            </>
          )}

          {/* Line with segment coloring based on destination */}
          <Line
            type="monotone"
            dataKey="meter_factor_value"
            stroke="#1976d2"
            strokeWidth={2}
            shape={(props: any) => {
              const { points } = props;
              if (!points || points.length === 0) return null;

              // Generate path with proper curves
              let pathData = '';
              const segments = [];

              // Build the path and collect segment info
              for (let i = 0; i < points.length; i++) {
                if (i === 0) {
                  pathData += `M ${points[i].x} ${points[i].y}`;
                } else {
                  const prevPoint = points[i - 1];
                  const currPoint = points[i];

                  // Calculate control points for smooth curve
                  const tension = 0.15; // Reduced from 0.3 for smoother curves
                  let cp1x = prevPoint.x;
                  let cp1y = prevPoint.y;
                  let cp2x = currPoint.x;
                  let cp2y = currPoint.y;

                  if (i > 1) {
                    const ppPoint = points[i - 2];
                    cp1x = prevPoint.x + (currPoint.x - ppPoint.x) * tension;
                    cp1y = prevPoint.y + (currPoint.y - ppPoint.y) * tension;
                  }

                  if (i < points.length - 1) {
                    const nextPoint = points[i + 1];
                    cp2x = currPoint.x - (nextPoint.x - prevPoint.x) * tension;
                    cp2y = currPoint.y - (nextPoint.y - prevPoint.y) * tension;
                  }

                  // Store segment info for coloring
                  const destIsOut = currPoint.payload.is_out_of_control;
                  segments.push({
                    path: `M ${prevPoint.x} ${prevPoint.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${currPoint.x} ${currPoint.y}`,
                    color: destIsOut ? '#f44336' : '#1976d2'
                  });
                }
              }

              return (
                <g>
                  {/* Draw colored line segments */}
                  {segments.map((segment, index) => (
                    <path
                      key={`segment-${index}`}
                      d={segment.path}
                      stroke={segment.color}
                      strokeWidth={2}
                      fill="none"
                    />
                  ))}

                  {/* Draw dots */}
                  {points.map((point: any, index: number) => {
                    const isOut = point.payload.is_out_of_control;
                    return (
                      <circle
                        key={`dot-${index}`}
                        cx={point.x}
                        cy={point.y}
                        r={3}
                        fill={isOut ? '#f44336' : '#1976d2'}
                        stroke={isOut ? '#f44336' : '#1976d2'}
                        strokeWidth={1}
                      />
                    );
                  })}
                </g>
              );
            }}
            dot={false}
            activeDot={{ r: 4 }}
            legendType="none"
          />

          {/* Simplified axes - no labels to save space */}
          <XAxis
            dataKey="timestamp"
            hide={true}
          />
          <YAxis
            domain={[
              (dataMin: number) => {
                // Find the min/max considering both meter_factor and control limits
                const factors = sortedData.slice(-5).map(d => d.meter_factor);
                const lcl = data[0]?.lcl || dataMin;
                const ucl = data[0]?.ucl || dataMin;

                const minFactor = Math.min(...factors);
                const maxFactor = Math.max(...factors);

                // Check if any values are outside control limits
                const belowLCL = minFactor < lcl;
                const aboveUCL = maxFactor > ucl;

                if (belowLCL || aboveUCL) {
                  // Calculate symmetric padding
                  const distanceBelowLCL = belowLCL ? lcl - minFactor : 0;
                  const distanceAboveUCL = aboveUCL ? maxFactor - ucl : 0;
                  const maxDistance = Math.max(distanceBelowLCL, distanceAboveUCL);
                  const padding = maxDistance + (ucl - lcl) * 0.1;

                  return Math.floor((lcl - padding) * 10000) / 10000;
                } else {
                  // Normal padding when within limits
                  return Math.floor((lcl - 0.0005) * 10000) / 10000;
                }
              },
              (dataMax: number) => {
                // Find the min/max considering both meter_factor and control limits
                const factors = sortedData.slice(-5).map(d => d.meter_factor);
                const lcl = data[0]?.lcl || dataMax;
                const ucl = data[0]?.ucl || dataMax;

                const minFactor = Math.min(...factors);
                const maxFactor = Math.max(...factors);

                // Check if any values are outside control limits
                const belowLCL = minFactor < lcl;
                const aboveUCL = maxFactor > ucl;

                if (belowLCL || aboveUCL) {
                  // Calculate symmetric padding
                  const distanceBelowLCL = belowLCL ? lcl - minFactor : 0;
                  const distanceAboveUCL = aboveUCL ? maxFactor - ucl : 0;
                  const maxDistance = Math.max(distanceBelowLCL, distanceAboveUCL);
                  const padding = maxDistance + (ucl - lcl) * 0.1;

                  return Math.ceil((ucl + padding) * 10000) / 10000;
                } else {
                  // Normal padding when within limits
                  return Math.ceil((ucl + 0.0005) * 10000) / 10000;
                }
              }
            ]}
            hide={true}
          />

          <RechartsTooltip content={<CustomTooltip />} />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Anomaly indicator */}
      {statistics && statistics.anomaly_count > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Chip
            label={`${statistics.anomaly_count} consecutive`}
            size="small"
            color="warning"
            sx={{ height: 16, fontSize: '0.65rem' }}
            title={`Current reading out of range for ${statistics.anomaly_count} consecutive proving${statistics.anomaly_count > 1 ? 's' : ''}`}
          />
        </Box>
      )}
    </Box>
  );
};

export default MiniMeterChart;